/**
 * FAO GeoID API Integration Utility
 *
 * Registers field boundaries with the FAO GeoID registry and resolves geoids
 * back to GeoJSON. Replaces the AgStack asset registry for new registrations;
 * `utils/AgStackAPI` is kept read-only so previously shared AgStack ids still
 * resolve.
 *
 * Calls go through the `registerGeoId` / `fetchGeoId` cloud functions rather
 * than straight to the registry. That is not a preference — the GeoID API sends
 * no CORS headers (a preflighted POST gets a 405, and even a plain GET comes
 * back with no `Access-Control-Allow-Origin`), so the browser cannot reach it
 * directly. The proxy holds no credential: the reserved `public` collection
 * accepts anonymous writes, so it is a CORS shim only.
 *
 * The proxy relays the registry's status and body verbatim, so the duplicate
 * handling below reads exactly as it would against the registry itself.
 *
 * Verified against the live API v0.8.0. Where the bundled user guide
 * (earth-map-cloud-functions/docs/GEOID API_GUIDE.html) disagrees with the
 * deployment, this follows the deployment.
 *
 * @class GeoIdAPI
 */

import { callGeoIdFunction } from "api/adminUtils";

// Geoids are UUIDs, but the version nibble varies (v8 is minted today, the API
// guide's examples are v7), so it must not be pinned.
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// AgStack ids are 64 hex chars — no overlap with the UUID shape above, so a
// caller can route an id to the right registry on shape alone.
const AGSTACK_ID_PATTERN = /^[0-9a-f]{64}$/i;

/**
 * True when `id` looks like a FAO geoid.
 *
 * @param {*} id
 * @returns {boolean}
 */
export const isGeoId = (id) =>
  typeof id === "string" && UUID_PATTERN.test(id.trim());

/**
 * True when `id` looks like a legacy AgStack geo id.
 *
 * @param {*} id
 * @returns {boolean}
 */
export const isAgStackId = (id) =>
  typeof id === "string" && AGSTACK_ID_PATTERN.test(id.trim());

class GeoIdAPI {
  /**
   * Register a polygon and return its geoid.
   *
   * A duplicate geometry is treated as success: geoids are content-addressed
   * (UUIDv8 derived from the geometry), so the incumbent the registry names in
   * its 409 is exactly what a fresh mint would have produced.
   *
   * @param {Object} geoJson - A GeoJSON Feature, FeatureCollection, or geometry
   * @returns {Promise<string|{errorMessage: string}>} The geoid, or an error object
   * @throws {Error} If the input is not usable GeoJSON
   */
  async registerGeoJson(geoJson) {
    if (!geoJson || typeof geoJson !== "object") {
      throw new Error("[GeoIdAPI] Invalid GeoJSON: not an object");
    }

    let feature = geoJson;

    if (feature.type === "FeatureCollection") {
      if (!feature.features || feature.features.length === 0) {
        throw new Error("[GeoIdAPI] FeatureCollection is empty");
      }
      // The registry mints one geoid per geometry; the boundary flows that call
      // this only ever carry a single polygon.
      if (feature.features.length > 1) {
        console.warn(
          "[GeoIdAPI] FeatureCollection has multiple features. Using the first.",
        );
      }
      feature = feature.features[0];
    }

    // Accept a bare geometry as well as a Feature.
    const geometry = feature.geometry || (feature.coordinates ? feature : null);
    if (!geometry || !geometry.type || !geometry.coordinates) {
      throw new Error("[GeoIdAPI] Invalid GeoJSON: no geometry found");
    }

    try {
      // `id` is deliberately omitted: it would become the external_id, which
      // must be unique within the collection, and we share `public` with every
      // other client. `properties` is required by the registry's PlaceCreate
      // schema even when empty.
      const { status, body } = await callGeoIdFunction("registerGeoId", {
        feature: {
          type: "Feature",
          geometry,
          properties: { source: "earthmap" },
        },
      });

      if (status >= 200 && status < 300) {
        if (body?.geoid) {
          return body.geoid;
        }
        return {
          errorMessage: body?.message || "No geoid in registry response",
        };
      }

      if (status === 409 && body?.geoid) {
        return body.geoid;
      }

      // A 409 can withhold the incumbent when its collection is not
      // public_read, leaving nothing usable to store.
      return {
        errorMessage:
          body?.reason ||
          body?.message ||
          `Registration failed with status ${status}`,
      };
    } catch (error) {
      console.error("[GeoIdAPI] Error registering boundary:", error);
      return {
        errorMessage: error.message || "Unknown error during registration",
      };
    }
  }

  /**
   * Resolve a geoid to its GeoJSON Feature.
   *
   * Note that an anonymous read returns the geometry but not the properties
   * that were submitted with it — the registry only serves full bodies to
   * members of the owning collection.
   *
   * @param {string} geoId
   * @returns {Promise<Object>} The GeoJSON Feature
   * @throws {Error} If the geoid cannot be resolved
   */
  async getGeoJSON(geoId) {
    const { status, body } = await callGeoIdFunction("fetchGeoId", { geoId });

    if (status < 200 || status >= 300) {
      throw new Error(
        body?.message || `HTTP ${status}: failed to resolve ${geoId}`,
      );
    }

    return body;
  }
}

export default GeoIdAPI;
